/**
 * 出金申請キャンセル処理
 *
 * 履歴画面から出金申請をキャンセルする際の確認ダイアログとキャンセル実行を担当。
 *
 * 操作するDOM要素:
 *   payoutRequestCancelDialogTarget    - キャンセル確認ダイアログ
 *   payoutRequestCancelDateTarget      - ダイアログ内の申請日表示
 *   payoutRequestCancelAmountTarget    - ダイアログ内の金額表示
 *
 * 使用するStimulus values:
 *   cancelPayoutRequestBasePathValue   - キャンセルAPIベースパス
 *
 * 内部状態:
 *   _pendingCancelId                   - キャンセル対象の出金申請ID
 *
 * 依存するメソッド（他のMixinから）:
 *   reloadHarvestHistoryFrame()        - 収穫残高履歴フレームリロード（TurboHelpersMixin）
 *    */
import axios from "@/modules/axios/index";
import { Turbo } from "@hotwired/turbo-rails";

export const PayoutRequestCancelHandlingMixin = {
  /**
   * キャンセル確認ダイアログを表示
   * トリガー: 履歴画面のキャンセルボタンclickイベント
   * HTML: components/_payout_request_history_content.html.erb
   *       data-action="click->harvest-warehouse#showPayoutRequestCancelDialog"
   */
  showPayoutRequestCancelDialog(event) {
    const button = event.currentTarget;
    const { payoutRequestId, amountFormatted, date } = button.dataset;

    if (!payoutRequestId) {
      console.error("Payout request ID not found");
      return;
    }

    // Store pending cancel ID for confirmPayoutRequestCancel
    this._pendingCancelId = payoutRequestId;

    // Populate dialog content
    if (this.hasPayoutRequestCancelDateTarget) {
      this.payoutRequestCancelDateTarget.textContent = date;
    }
    if (this.hasPayoutRequestCancelAmountTarget) {
      this.payoutRequestCancelAmountTarget.textContent = amountFormatted;
    }

    // Show dialog
    if (this.hasPayoutRequestCancelDialogTarget) {
      this.payoutRequestCancelDialogTarget.style.display = "flex";
    }
  },

  /**
   * キャンセル確認ダイアログを閉じる
   * トリガー: いいえボタンclickイベント
   * HTML: components/_payout_request_cancel_dialog.html.erb
   */
  closePayoutRequestCancelDialog() {
    if (this.hasPayoutRequestCancelDialogTarget) {
      this.payoutRequestCancelDialogTarget.style.display = "none";
    }
    this._pendingCancelId = null;
  },

  /**
   * 出金申請キャンセル実行（AJAX）
   * トリガー: 確認ダイアログのはいボタンclickイベント
   * HTML: components/_payout_request_cancel_dialog.html.erb
   *       data-action="click->harvest-warehouse#confirmPayoutRequestCancel"
   */
  async confirmPayoutRequestCancel(event) {
    const button = event.currentTarget;
    const payoutRequestId = this._pendingCancelId;

    if (!payoutRequestId) {
      console.error("Payout request ID not found");
      return;
    }

    button.disabled = true;

    const url = this.cancelPayoutRequestBasePathValue.replace("PAYOUT_REQUEST_ID", payoutRequestId);

    const afterSuccess = (response) => {
      Turbo.renderStreamMessage(response.data);
      this.reloadPayoutRequestHistoryFrame();
      this.reloadHarvestHistoryFrame();
    };

    const catchError = (error) => {
      console.error("Cancel payout request failed:", error);
      // NOTE: キャンセルダイアログ表示中はフッターが非表示のため、通知は不要
    };

    await axios
      .delete(url, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
      })
      .then(afterSuccess)
      .catch(catchError)
      .finally(() => {
        button.disabled = false;
        this.closePayoutRequestCancelDialog();
      });
  },
};
